import { useCallback, useState } from "react";
import { EquipmentBack, useEquipmentNavigation } from "./EquipmentNavigation";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Badge } from "@/components/ui/badge";
import { datum } from "@/lib/format";
import { EquipmentScanButton } from "./EquipmentScanner";
import { Feedback, useResource } from "@/components/workflow/shared";
import {
  siteEquipmentApi,
  type EquipmentArticle,
  type EquipmentMovementKind,
  type EquipmentResult,
  type EquipmentTransfer,
} from "@/lib/api/oprema";
import {
  Condition,
  History,
  Ownership,
  PlaceStatus,
  UndoNotice,
  useEquipmentAccess,
  EquipmentRouteButton,
} from "./shared";
import { EquipmentDetail, EquipmentList } from "./EquipmentList";
import { EquipmentDraft, type EquipmentDraftCache } from "./EquipmentDraft";
import { EquipmentDocumentActions } from "./EquipmentDocumentActions";
import { EquipmentShortages } from "./EquipmentShortages";
import { EquipmentTasks } from "./EquipmentTasks";
import {
  MovementForm,
  ReceiptForm,
  TransferForm,
  placeTargets,
  missingTargets,
  type MovementTarget,
  targetKey,
} from "./EquipmentForms";

export function EquipmentPlaceView({
  placeId,
  siteId,
}: {
  placeId?: string;
  siteId?: string;
}) {
  const access = useEquipmentAccess();
  const [createdArticles, setCreatedArticles] = useState<EquipmentArticle[]>([]);
  const articleCreated = (article: EquipmentArticle) => setCreatedArticles((old) => [...old, article]);
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const navigation = useEquipmentNavigation();
  const params = navigation.params;
  const detailKey = params.get("detalj") ?? "";
  const setDetailKey = (value: string) =>
    value
      ? navigation.open({ detalj: value })
      : navigation.close(["detalj", "artikl"]);
  const formTarget = params.get("stavka") ?? "";
  const [otherActions, setOtherActions] = useState(false);
  const [journalOpen, setJournalOpen] = useState(false);
  const resource = useResource(
    `${placeId}:${siteId}:${access.read}`,
    useCallback(
      async (signal) => {
        if (!access.read) return null;
        const [data, places, articles, missing] = await Promise.all([
          siteId
            ? siteEquipmentApi.site(siteId, signal)
            : siteEquipmentApi.place(placeId!, signal),
          siteEquipmentApi.places(signal, access.move || access.maintain),
          siteEquipmentApi.articles(signal),
          siteEquipmentApi.missing(signal),
        ]);
        const missingHere = missing.filter(
          (m) => m.mjesto_id === data.mjesto.id,
        );
        const lostPieces = await Promise.all(
          [
            ...new Set(
              missingHere.flatMap((m) => (m.komad_id ? [m.komad_id] : [])),
            ),
          ].map((id) => siteEquipmentApi.piece(id, signal)),
        );
        return {
          data,
          places,
          articles,
          missingHere,
          lostPieces: lostPieces.map((d) => d.komad),
        };
      },
      [placeId, siteId, access.read, access.move, access.maintain],
    ),
  );
  const requestedForm = params.get("radnja");
  const form = [
    "transfer",
    "pocetno",
    "ostecenje",
    "u_radionu",
    "iz_radione",
    "otpis",
    "nestalo",
    "pronadeno",
    "produljenje_roka",
  ].includes(requestedForm ?? "")
    ? (requestedForm as "transfer" | EquipmentMovementKind)
    : null;
  const setForm = (value: "transfer" | EquipmentMovementKind | null) =>
    value
      ? navigation.open({ radnja: value, detalj: null, primitak: null })
      : navigation.close(["radnja", "stavka"]);
  const receipt =
    resource.data?.data.stize.find((t) => t.id === params.get("primitak")) ??
    null;
  const setReceipt = (value: EquipmentTransfer | null) =>
    value
      ? navigation.open({ primitak: value.id, radnja: null, detalj: null })
      : navigation.close(["primitak"]);
  const [operation, setOperation] = useState<{
    result: EquipmentResult;
    message: string;
  } | null>(null);
  const [historyId, setHistoryId] = useState("");
  const history = useResource(
    `${historyId}:${placeId}:${siteId}`,
    useCallback(
      (signal) =>
        historyId
          ? siteEquipmentApi.history(
              historyId,
              resource.data?.data.mjesto.id,
              signal,
            )
          : Promise.resolve([]),
      [historyId, resource.data?.data.mjesto.id],
    ),
  );
  const saved = (result: EquipmentResult, message: string) => {
    setOperation({
      result,
      message:
        result.status === "ceka_potvrdu" ? "Čeka potvrdu uprave." : message,
    });
    drafts.clear();
    navigation.close([
      "radnja",
      "primitak",
      "detalj",
      "stavka",
      "artikl",
      "manjak",
      "rjesenje",
    ]);
    resource.reload();
  };
  if (!access.read) return <p role="alert">Nemate ovlast za pregled opreme.</p>;
  const current = resource.data;
  return (
    <section className="site-equipment" aria-label="Oprema gradilišta">
      <EquipmentBack
        crumbs={
          current && (form || receipt)
            ? [
                {
                  label: current.data.mjesto.naziv,
                  to: `/oprema/mjesta/${current.data.mjesto.id}`,
                },
              ]
            : [{ label: "Mjesta", to: "/oprema/mjesta" }]
        }
      />
      <UndoNotice
        operation={operation}
        onUndone={() => {
          setOperation(null);
          resource.reload();
        }}
      />
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {resource.loading && <p role="status">Učitavanje opreme…</p>}
      {current &&
        (() => {
          const { data, places, missingHere, lostPieces } = current;
          const articles = [...current.articles, ...createdArticles.filter((a) => !current.articles.some((old) => old.id === a.id))];
          const targets = placeTargets(data);
          const startForm = (
            next: "transfer" | EquipmentMovementKind,
            target = "",
          ) =>
            navigation.open({
              radnja: next,
              stavka: target,
              detalj: null,
              artikl: null,
              primitak: null,
            });
          const initialTargets: MovementTarget[] = articles
            .filter((a) => a.aktivna)
            .flatMap((article) => {
              const balances = data.stanje.filter(
                (b) => b.artikl_id === article.id,
              );
              return article.nacin === "komad"
                ? [{ article }]
                : [
                    ...balances.map((balance) => ({ article, balance })),
                    ...(!balances.some((b) => b.najam_id === null)
                      ? [{ article }]
                      : []),
                  ];
            });
          const canSend = access.canSend(data.mjesto);
          const maintain = access.canMaintain(data.mjesto);
          const hasWorkshop =
            data.mjesto.vrsta === "radiona" ||
            targets.some((target) =>
              target.piece
                ? target.piece.stanje === "u_radioni"
                : target.balance!.u_radioni > 0,
            );
          const damageTargets = targets.filter((t) =>
            t.piece ? t.piece.stanje === "ispravno" : t.balance!.ispravno > 0,
          );
          const formTargets =
            form === "pronadeno"
              ? missingTargets(missingHere, articles, lostPieces)
              : form === "pocetno"
                ? initialTargets
                : form === "ostecenje"
                  ? damageTargets
                  : form === "iz_radione"
                    ? targets.filter((t) =>
                        t.piece
                          ? t.piece.stanje === "u_radioni"
                          : t.balance!.u_radioni > 0,
                      )
                    : targets.filter((t) =>
                        t.piece
                          ? (!["otpis", "nestalo"].includes(form ?? "") ||
                              !t.piece.ceka_otpis) &&
                            (form === "u_radionu"
                              ? ["ispravno", "osteceno"]
                              : ["ispravno", "osteceno", "u_radioni"]
                            ).includes(t.piece.stanje)
                          : t.balance!.ispravno +
                              t.balance!.osteceno +
                              (form === "u_radionu"
                                ? 0
                                : t.balance!.u_radioni) >
                            0,
                      );
          const emptyNext = (
            <>
              {data.stize.map((transfer) => (
                <div className="equipment-field" key={transfer.id}>
                  <p>
                    {transfer.stavke.reduce(
                      (total, line) => total + line.poslano,
                      0,
                    )}{" "}
                    kom je u prijevozu – čeka potvrdu primitka.
                  </p>
                  {access.receive && (
                    <Button onClick={() => setReceipt(transfer)}>
                      Potvrdi primitak
                    </Button>
                  )}
                </div>
              ))}
              {!data.stize.length && (
                <p>
                  {form === "pocetno"
                    ? "Prvo dodaj vrstu opreme, npr. okvir skele."
                    : "Provjeri drugo mjesto ili prvo popiši opremu."}
                </p>
              )}
              {access.catalogue && !articles.length && !["pocetno", "ostecenje", "transfer"].includes(form ?? "") && (
                <EquipmentRouteButton to="/oprema/artikli?uredi=new">
                  Novi artikl
                </EquipmentRouteButton>
              )}
            </>
          );
          if (form === "u_radionu")
            formTargets.sort(
              (a, b) =>
                Number(
                  !!(b.piece?.stanje === "osteceno" || b.balance?.osteceno),
                ) -
                Number(
                  !!(a.piece?.stanje === "osteceno" || a.balance?.osteceno),
                ),
            );
          return (
            <>
              <div hidden={!!form || !!receipt} className="equipment-field">
                <div className="equipment-row">
                  <h2 className="text-xl font-bold">{data.mjesto.naziv}</h2>
                  <PlaceStatus place={data.mjesto} />
                </div>
                <EquipmentTasks placeId={data.mjesto.id} />
                {(data.stize.length > 0 ||
                  data.upozorenja.length > 0 ||
                  (data.trazi_se?.length ?? 0) > 0) && (
                  <h3 className="equipment-label">Treba odraditi</h3>
                )}
                {data.stize.map((transfer) => (
                  <div
                    className="equipment-todo equipment-info"
                    key={transfer.id}
                  >
                    <b>
                      Stiže iz{" "}
                      {places.find((p) => p.id === transfer.iz_mjesta_id)
                        ?.naziv ?? "drugog mjesta"}
                    </b>
                    <p>
                      {transfer.stavke
                        .map(
                          (s) =>
                            `${s.poslano} ${articles.find((a) => a.id === s.artikl_id)?.naziv ?? "kom"}`,
                        )
                        .join(" · ")}
                    </p>
                    <p>Poslao {transfer.poslao}</p>
                    {transfer.primio && <p>Primio {transfer.primio}</p>}
                    {access.receive && (
                      <Button
                        onClick={() => {
                          setReceipt(transfer);
                        }}
                      >
                        Stiglo – potvrdi primitak
                      </Button>
                    )}
                  </div>
                ))}
                <EquipmentDraft cache={drafts} name="shortages">
                  <EquipmentShortages
                    rows={data.trazi_se ?? []}
                    articles={articles}
                    canResolve={canSend}
                    onSaved={saved}
                  />
                </EquipmentDraft>
                {data.upozorenja.map((warning, index) => (
                  <div
                    className={
                      "equipment-todo " +
                      (warning.startsWith("Bez potpisa")
                        ? "equipment-error"
                        : "equipment-warning")
                    }
                    key={index}
                  >
                    <span>{warning}</span>
                    <Button
                      variant="outline"
                      onClick={() => setJournalOpen(true)}
                    >
                      Pogledaj
                    </Button>
                  </div>
                ))}
              </div>
              {!form && !receipt && (
                <>
                  <h3 className="equipment-label">Radnje</h3>
                  <div className="equipment-place-actions">
                    {canSend && (
                      <Button onClick={() => startForm("transfer")}>
                        <Icon name="move" /> Preseli
                      </Button>
                    )}
                    <EquipmentScanButton />
                    {access.damage && (
                      <Button
                        variant="outline"
                        onClick={() => startForm("ostecenje")}
                      >
                        <Icon name="alert" /> Oštećenje
                      </Button>
                    )}
                    {maintain && (
                      <Button
                        variant="outline"
                        onClick={() => startForm("u_radionu")}
                      >
                        <Icon name="settings" /> Pošalji u radionu
                      </Button>
                    )}
                    {maintain && (
                      <Button
                        variant="outline"
                        onClick={() => startForm("pocetno")}
                      >
                        <Icon name="pencil" /> Popiši
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="equipment-more"
                      aria-expanded={otherActions}
                      onClick={() => setOtherActions((old) => !old)}
                    >
                      {otherActions ? "Manje radnji ▴" : "Više radnji ▾"}
                    </Button>
                    {otherActions && (
                      <div className="equipment-morebox">
                        <EquipmentRouteButton to={`/oprema/mjesta/${data.mjesto.id}/inventura`}>Inventura</EquipmentRouteButton>
                        {maintain && <EquipmentRouteButton to={`/oprema/najam?mjesto=${data.mjesto.id}&radnja=return`}>Vrati najmodavcu</EquipmentRouteButton>}
                        {data.mjesto.vrsta === "gradiliste" && <>
                          {maintain && <EquipmentRouteButton to={`/oprema/mjesta/${data.mjesto.id}/zahtjevi`}>Zatraži iz Baze</EquipmentRouteButton>}
                          <EquipmentRouteButton to={`/oprema/mjesta/${data.mjesto.id}/zatvaranje`}>Zatvaranje gradilišta</EquipmentRouteButton>
                        </>}
                        {maintain &&
                          (
                            [
                              "iz_radione",
                              "otpis",
                              "nestalo",
                              "pronadeno",
                            ] as const
                          )
                            .filter(
                              (kind) => kind !== "iz_radione" || hasWorkshop,
                            )
                            .map((kind) => (
                              <Button
                                variant="outline"
                                key={kind}
                                onClick={() => startForm(kind)}
                              >
                                {
                                  {
                                    iz_radione: "Vraćeno iz radione",
                                    otpis: "Otpiši",
                                    nestalo: "Nestalo",
                                    pronadeno: "Pronađeno",
                                  }[kind]
                                }
                              </Button>
                            ))}
                        <Button
                          variant="outline"
                          aria-expanded={journalOpen}
                          onClick={() => setJournalOpen((old) => !old)}
                        >
                          Dnevnik mjesta
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => window.print()}
                        >
                          Ispiši popis
                        </Button>
                      </div>
                    )}
                  </div>
                  {journalOpen && (
                    <section
                      className="equipment-card"
                      aria-label="Dnevnik mjesta"
                    >
                      <h3>Dnevnik mjesta</h3>
                      <History events={data.povijest ?? []} places={places} />
                    </section>
                  )}
                </>
              )}
              {receipt && access.receive && (
                <EquipmentDraft
                  key={`receipt:${receipt.id}`}
                  cache={drafts}
                  name={`receipt:${receipt.id}`}
                >
                  <ReceiptForm
                    key={receipt.id}
                    transfer={receipt}
                    articles={articles}
                    pieces={data.komadi}
                    onSaved={saved}
                    onClose={() => setReceipt(null)}
                  />
                </EquipmentDraft>
              )}
              {form === "transfer" && canSend && (
                <EquipmentDraft
                  key={`transfer:${formTarget}`}
                  cache={drafts}
                  name={`transfer:${formTarget}`}
                >
                  <TransferForm
                    articles={articles}
                    onArticleCreated={articleCreated}
                    key={formTarget}
                    initialTarget={formTarget || undefined}
                    emptyNext={emptyNext}
                    data={data}
                    places={places}
                    onSaved={saved}
                    onClose={() => setForm(null)}
                  />
                </EquipmentDraft>
              )}
              {form &&
                form !== "transfer" &&
                (form === "ostecenje" ? access.damage : maintain) && (
                  <EquipmentDraft
                    key={`${form}:${formTarget}`}
                    cache={drafts}
                    name={`${form}:${formTarget}`}
                  >
                    <MovementForm
                    articles={articles}
                    onArticleCreated={articleCreated}
                    hasEquipment={targets.some((t) => t.piece ? ["ispravno", "osteceno", "u_radioni"].includes(t.piece.stanje) : t.balance!.ispravno + t.balance!.osteceno + t.balance!.u_radioni > 0)}
                      key={`${form}:${formTarget}`}
                      initialTarget={formTarget || undefined}
                      emptyNext={emptyNext}
                      kind={form}
                      targets={formTargets}
                      place={data.mjesto}
                      places={places}
                      onSaved={saved}
                      onClose={() => setForm(null)}
                    />
                  </EquipmentDraft>
                )}
              <div hidden={!!form || !!receipt}>
                <EquipmentList
                  alwaysSearch
                  label="Stanje mjesta"
                  onOpen={setDetailKey}
                  items={targets.map((target) => ({
                    id: targetKey(target),
                    name: target.article.naziv,
                    group: target.article.skupina,
                    piece: !!target.piece,
                    typeId: target.article.id,
                    subtitle: target.piece?.inventarni_broj,
                    count: target.piece ? undefined : target.balance!.ispravno,
                    countLabel:
                      !target.piece &&
                      target.balance!.ispravno === 0 &&
                      target.balance!.u_prijevozu > 0
                        ? `0 ovdje · +${target.balance!.u_prijevozu} stiže`
                        : undefined,
                    badges: target.piece ? (
                      <>
                        <Condition value={target.piece.stanje} />
                        {target.piece.najam_id && (
                          <Ownership item={target.piece} />
                        )}
                        {target.piece.ceka_otpis && (
                          <Badge tone="warn">čeka otpis</Badge>
                        )}
                      </>
                    ) : (
                      <>
                        {target.balance!.osteceno > 0 && (
                          <Badge tone="bad">
                            {target.balance!.osteceno} oštećeno
                          </Badge>
                        )}
                        {target.balance!.u_radioni > 0 && (
                          <Badge tone="warn">
                            {target.balance!.u_radioni} radiona
                          </Badge>
                        )}
                        {target.balance!.u_prijevozu > 0 && (
                          <Badge tone="info">
                            +{target.balance!.u_prijevozu} stiže
                          </Badge>
                        )}
                        {!!target.balance!.ceka_otpis && (
                          <Badge tone="warn">
                            {target.balance!.ceka_otpis} čeka otpis
                          </Badge>
                        )}
                        {target.balance!.najam_id && (
                          <Ownership item={target.balance!} />
                        )}
                      </>
                    ),
                  }))}
                />
              </div>
              {(detailKey || (!form && params.get("artikl"))) &&
                (() => {
                  const target =
                    targets.find((t) => targetKey(t) === detailKey) ??
                    (!detailKey
                      ? targets.find(
                          (t) => t.article.id === params.get("artikl"),
                        )
                      : undefined);
                  if (!target) return null;
                  const { piece, balance } = target;
                  const healthy = piece
                    ? piece.stanje === "ispravno"
                    : (balance?.ispravno ?? 0) > 0;
                  const active = piece
                    ? ["ispravno", "osteceno"].includes(piece.stanje)
                    : balance!.ispravno + balance!.osteceno > 0;
                  const pending = piece?.ceka_otpis;
                  const start = (next: "transfer" | EquipmentMovementKind) => {
                    startForm(next, targetKey(target));
                  };
                  return (
                    <EquipmentDetail
                      crumbs={[
                        {
                          label: data.mjesto.naziv,
                          to: `/oprema/mjesta/${data.mjesto.id}`,
                        },
                      ]}
                      title={target.article.naziv}
                      onClose={() => {
                        navigation.close(["detalj", "artikl"]);
                        setHistoryId("");
                      }}
                    >
                      {piece ? (
                        <>
                          <p>{piece.inventarni_broj}</p>
                          <Condition value={piece.stanje} />
                        </>
                      ) : (
                        <div className="equipment-summary">
                          <div>
                            Ispravno<strong>{balance!.ispravno}</strong>
                          </div>
                          <div>
                            Oštećeno<strong>{balance!.osteceno}</strong>
                          </div>
                          {balance!.u_radioni > 0 && (
                            <div>
                              U radioni<strong>{balance!.u_radioni}</strong>
                            </div>
                          )}
                          <div>
                            U prijevozu<strong>{balance!.u_prijevozu}</strong>
                          </div>
                        </div>
                      )}
                      <Ownership item={piece ?? balance!} />
                      <div className="equipment-actions">
                        {canSend && healthy && !pending && (
                          <Button onClick={() => start("transfer")}>
                            Preseli
                          </Button>
                        )}
                        {access.damage && healthy && !pending && (
                          <Button
                            variant="outline"
                            onClick={() => start("ostecenje")}
                          >
                            Oštećenje
                          </Button>
                        )}
                        {maintain && active && !pending && (
                          <Button
                            variant="outline"
                            onClick={() => start("u_radionu")}
                          >
                            Pošalji u radionu
                          </Button>
                        )}
                        {maintain &&
                          (piece
                            ? piece.stanje === "u_radioni"
                            : balance!.u_radioni > 0) &&
                          !pending && (
                            <Button onClick={() => start("iz_radione")}>
                              Vraćeno iz radione
                            </Button>
                          )}
                        {maintain &&
                          !pending &&
                          !(piece?.najam_id ?? balance?.najam_id) &&
                          (active ||
                            (piece
                              ? piece.stanje === "u_radioni"
                              : balance!.u_radioni > 0)) && (
                            <Button
                              variant="outline"
                              onClick={() => start("otpis")}
                            >
                              Otpiši
                            </Button>
                          )}
                        {maintain && (
                          <Button
                            variant="outline"
                            onClick={() => start("pocetno")}
                          >
                            Popiši
                          </Button>
                        )}
                        {piece ? (
                          <EquipmentRouteButton
                            to={`/oprema/komadi/${piece.id}`}
                          >
                            Povijest komada
                          </EquipmentRouteButton>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => setHistoryId(target.article.id)}
                          >
                            Povijest
                          </Button>
                        )}
                      </div>
                      <h3 className="font-bold">Zadnje promjene</h3>
                      <div className="equipment-recent">
                        {(data.povijest ?? [])
                          .filter(
                            (event) =>
                              event.artikl_id === target.article.id &&
                              (!piece || event.komad_id === piece.id),
                          )
                          .slice(0, 2)
                          .map((event) => (
                            <div key={event.id}>
                              <span>
                                {datum(event.datum)} · {event.kolicina} kom
                                {event.razlog ? ` · ${event.razlog}` : ""}
                              </span>
                              {event.dokument && (
                                <EquipmentDocumentActions
                                  compact
                                  document={event.dokument}
                                />
                              )}
                            </div>
                          ))}
                        {!(data.povijest ?? []).some(
                          (event) =>
                            event.artikl_id === target.article.id &&
                            (!piece || event.komad_id === piece.id),
                        ) && <p>Još nema zabilježenih promjena.</p>}
                      </div>
                      {historyId === target.article.id && (
                        <>
                          <Feedback error={history.error} />
                          {history.loading ? (
                            <p role="status">Učitavanje povijesti…</p>
                          ) : (
                            <History
                              events={history.data ?? []}
                              places={places}
                            />
                          )}
                        </>
                      )}
                    </EquipmentDetail>
                  );
                })()}
            </>
          );
        })()}
    </section>
  );
}
